import { useState, type FormEvent } from 'react';
import { api, ApiError, type CurrentUser } from '../api/client.js';
import { useRefreshSession } from '../auth/useSession.js';
import { Alert, Button, Card, Field, Input, PageHeader } from '../components/ui.js';
import { PasswordChecklist, passwordMeetsPolicy } from '../components/PasswordChecklist.js';
import { RecoveryCodes } from '../components/RecoveryCodes.js';

/**
 * Your own account: who the app thinks you are, and the two credentials you
 * can replace without anyone's help.
 *
 * Both live here, each behind the current password, because neither is a
 * decision anybody but the account holder should be involved in. There is no
 * forgot-password email in this app, so being able to rotate a password from
 * inside a live session is the ordinary way it gets changed.
 */
export function AccountPage({
  user,
  unusedRecoveryCodes,
}: {
  user: CurrentUser;
  unusedRecoveryCodes: number;
}) {
  return (
    <div className="space-y-4">
      <PageHeader title="Account" description="Your sign-in details and how you get back in." />

      <Card className="p-5">
        <h2 className="text-sm font-medium text-body">Signed in as</h2>
        <dl className="mt-3 grid gap-3 sm:grid-cols-3">
          <Detail label="Name" value={user.displayName} />
          <Detail label="Email" value={user.email} />
          <Detail label="Role" value={user.roleLabel} />
        </dl>
        <p className="mt-3 text-xs text-muted">
          Your name, email address, and role are set by an administrator.
        </p>
      </Card>

      <ChangePasswordCard />
      <RecoveryCodesCard unusedRecoveryCodes={unusedRecoveryCodes} />
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-strong">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Password
// ---------------------------------------------------------------------------

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [busy, setBusy] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const meetsPolicy = passwordMeetsPolicy(newPassword);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match.');
      return;
    }

    setError(null);
    setChanged(false);
    setBusy(true);

    try {
      await api.post('/api/auth/account/password', { currentPassword, newPassword });
      // Nothing is left on screen that a shoulder-surfer could use, and an
      // empty form is the clearest signal the change went through.
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setChanged(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Change your password</h2>
      <p className="mt-0.5 text-xs text-muted">
        This window stays signed in; every other session and remembered device ends.
      </p>

      <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 max-w-sm space-y-4">
        {error && <Alert>{error}</Alert>}
        {changed && <Alert tone="success">Your password has been changed.</Alert>}

        <Field label="Current password">
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={busy}
          />
        </Field>

        <div>
          <Field label="New password">
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={busy}
            />
          </Field>
          <PasswordChecklist password={newPassword} />
        </div>

        <Field label="Confirm new password">
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
            disabled={busy}
          />
        </Field>

        {mismatch && <p className="text-xs text-danger-text">The two passwords do not match.</p>}

        <Button type="submit" disabled={busy || mismatch || !meetsPolicy}>
          {busy ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Recovery codes
// ---------------------------------------------------------------------------

interface RecoveryCodesResponse {
  recoveryCodes: string[];
}

function RecoveryCodesCard({ unusedRecoveryCodes }: { unusedRecoveryCodes: number }) {
  const refreshSession = useRefreshSession();
  const [asking, setAsking] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const result = await api.post<RecoveryCodesResponse>('/api/auth/account/recovery-codes', {
        password,
        code,
      });
      setCodes(result.recoveryCodes);
      setPassword('');
      setCode('');
      setAsking(false);
      // The low-codes banner reads its count from the session.
      await refreshSession();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not generate a new set.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Recovery codes</h2>
      <p className="mt-0.5 text-xs text-muted">
        The way back in if you lose your authenticator. Each code works once.
      </p>

      <p className="mt-3 text-sm text-body">
        {unusedRecoveryCodes === 0
          ? 'You have no unused recovery codes left.'
          : `You have ${unusedRecoveryCodes} unused recovery code${
              unusedRecoveryCodes === 1 ? '' : 's'
            }.`}
      </p>

      {codes ? (
        <div className="mt-4 max-w-sm space-y-4">
          <Alert tone="warning">
            This is the only time these will be shown, and your previous codes no longer work. Store
            them somewhere safe — a password manager is ideal. Each one works once.
          </Alert>
          <RecoveryCodes codes={codes} filename="recovery-codes.txt" />
        </div>
      ) : asking ? (
        <form onSubmit={(e) => void handleSubmit(e)} className="mt-4 max-w-sm space-y-4">
          {error && <Alert>{error}</Alert>}

          <Alert tone="warning">
            Generating a new set invalidates every code you have now, used or not.
          </Alert>

          <Field label="Current password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
              disabled={busy}
            />
          </Field>

          <Field
            label="Code from your authenticator"
            hint="A live six-digit code. A recovery code will not do here."
          >
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              required
              disabled={busy}
              className="tabular tracking-widest"
            />
          </Field>

          <div className="flex gap-2">
            <Button type="submit" disabled={busy}>
              {busy ? 'Generating…' : 'Generate a new set'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setAsking(false);
                setError(null);
                setPassword('');
                setCode('');
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="mt-4">
          <Button type="button" variant="secondary" onClick={() => setAsking(true)}>
            Generate a new set
          </Button>
        </div>
      )}
    </Card>
  );
}
